/**
 * 3D-styled SVG Illustrations and Decorations for the Start / Landing Screen
 * matching the Kalqy design specifications.
 */

/**
 * Hangs from the very top of the page — must be placed inside a `relative`
 * full-page container, not inside `<main>` (which starts below the header).
 */
export function KalqyHangingSign() {
  return (
    <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-center select-none">
      <img
        src="/images/kalqy-hanging-sign.png"
        alt="Kalqy"
        className="h-auto w-64 object-contain drop-shadow-sm sm:w-[min(22rem,21dvh)]"
      />
    </div>
  );
}

/**
 * A heading flanked by the wordmark's own accent strokes.
 *
 * ⚠️ IT TAKES ITS TITLE NOW, BECAUSE A1 NO LONGER ASKS THE QUESTION. The tiles
 * that "Who's here today?" introduced are gone — A1 is a Play card beside a
 * sign-in panel — so the sparks now frame the panel's own heading. `panel`
 * is the smaller pairing: a card heading, not a page one.
 */
export function TitleSparks({
  title = "Who’s here today?",
  size = "page",
}: {
  title?: string;
  size?: "page" | "panel";
} = {}) {
  const panel = size === "panel";
  const spark = panel ? "w-4 h-4 sm:w-5 sm:h-5" : "w-5 h-5 sm:w-6 sm:h-6";
  const heading = panel
    ? "text-xl sm:text-2xl text-[#153A60]"
    : "text-2xl sm:text-4xl text-[#332D27]";

  return (
    <div
      className={`flex items-center justify-center ${panel ? "gap-2" : "gap-3 mt-3 sm:mt-4"}`}
    >
      {/* Left accent spark */}
      <svg
        className={`${spark} text-[#00C4CC]`}
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M16 5L8 10L16 15"
          stroke="currentColor"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12 17L8 19L12 21"
          stroke="#FFC107"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>

      <h1
        className={`font-extrabold tracking-tight text-center font-kid ${heading}`}
      >
        {title}
      </h1>

      {/* Right accent spark */}
      <svg
        className={`${spark} text-[#82C91E]`}
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M8 5L16 10L8 15"
          stroke="currentColor"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12 17L16 19L12 21"
          stroke="#E64980"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

/** 3D Teacher Illustration Component */
export function TeacherIllustration() {
  return (
    <div className="relative w-44 h-40 sm:w-48 sm:h-44 flex items-center justify-center overflow-hidden rounded-2xl">
      <img
        src="/images/teacher-avatar.jpg"
        alt="Teacher"
        className="w-full h-full object-contain rounded-2xl drop-shadow-sm transition-transform duration-300 group-hover:scale-105"
      />
    </div>
  );
}

/** 3D Parent Family Illustration Component */
export function ParentIllustration() {
  return (
    <div className="relative w-44 h-40 sm:w-48 sm:h-44 flex items-center justify-center">
      {/* Floating 3D Heart badge on top right */}
      <div className="absolute -top-1 -right-1 z-10 w-9 h-9 rounded-full bg-gradient-to-tr from-[#FF2A6F] to-[#FF6596] shadow-md flex items-center justify-center animate-bounce-subtle">
        <svg className="w-5 h-5 fill-white" viewBox="0 0 24 24">
          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
        </svg>
      </div>

      {/* Parent avatar circle */}
      <div className="w-36 h-36 rounded-full overflow-hidden border-4 border-emerald-100/80 shadow-inner bg-emerald-50 flex items-center justify-center">
        <img
          src="/images/parent-avatar.jpg"
          alt="Parent Family"
          className="w-full h-full object-cover scale-110 transition-transform duration-300 group-hover:scale-115"
        />
      </div>
    </div>
  );
}

/** Play Card 3D Play Button Icon */
export function PlayCardIcon() {
  return (
    <div className="relative w-28 h-28 sm:w-32 sm:h-32 rounded-full bg-white shadow-[inset_0_-4px_6px_rgba(0,0,0,0.06),0_10px_20px_rgba(0,0,0,0.12)] flex items-center justify-center my-2 transition-transform duration-200 group-hover:scale-105">
      <svg
        className="w-12 h-12 sm:w-14 sm:h-14 fill-[#FF2A6F] translate-x-1 drop-shadow-sm"
        viewBox="0 0 24 24"
      >
        <path d="M8 5v14l11-7z" />
      </svg>
    </div>
  );
}

/** Background Hills and Flowers Component */
export function BackgroundLandscape() {
  return (
    <>
      {/*
        📋 A SUN SAT ON THIS RIDGE AND WAS REMOVED BY REQUEST — from both screens
        this component draws, `#/start` and `#/connect`.

        ⚠️ `.kid-sun-glow` IS STILL LIVE AND MUST NOT BE DELETED WITH IT.
        `KidBackground` draws the child dashboard's own sun with the same class,
        and `kid.css` carries its keyframes and both reduce-motion switches. This
        file was the second caller, never the definition.
      */}

      {/*
        The meadow this screen stands on.

        🔴 IT IS A PAINTED BAND NOW, NOT TWO BÉZIERS AND TWENTY-SIX CIRCLES. The
        hills were `preserveAspectRatio="none"`, which is exactly what a two-tone
        silhouette wants and exactly what a picture cannot survive: the SVG
        stretched to whatever the viewport was and nothing in it had a shape worth
        protecting. The flowers were the tell — five circles in a plus, at three
        sizes, six times — a daisy described by a computer rather than drawn.

        🔴 NO HEIGHT, NO CLIP, NO `object-cover` — THE BAND IS WHATEVER THE
        PICTURE IS. A first pass kept the SVG's `h-40 sm:h-52` and covered into
        it, which is the right fit for a photo behind a hero and the wrong one
        here: this artwork's whole silhouette IS the content, so cropping it took
        the tops off the tall leaf clusters at both ends and sliced the pink
        flower at the left edge in half. A fixed height plus `overflow-hidden`
        cannot show a picture whose shape is the point.

        `w-full h-auto` makes the height follow the width at the file's own
        ratio — 2172 × 462, so the band is always `100vw / 4.70` ≈ **21.3vw** tall.
        Nothing is cropped at any width, ever.

        📋 ANYTHING PUT BACK ON THIS HORIZON MUST BE MEASURED IN `vw` TOO. A sun
        used to sit here as a SIBLING of the band — both `z-0`, so the band,
        later in the DOM, covered its base and it read as resting on the ridge.
        That only works while the two share a unit: the band is `21.3vw` tall and
        its crest in the right-hand column is `9.3vw`, so a pixel offset lines up
        at exactly one window width and hides behind the hill at every other.

        ⚠️ `.webp`, AND THAT IS DOWNLOAD WEIGHT RATHER THAN TASTE. The render
        arrived as a 375 KB PNG; trimmed to its alpha bounds (2172 × 462 — the top
        262 rows were empty) WebP carries the same alpha in 70 KB. This decoration
        is on the sign-in doors and the plans page, which are the first screens a
        school ever loads.

        ⚠️ `aria-hidden` WITH AN EMPTY `alt`, LIKE EVERY OTHER NODE IN THIS FILE.
        Decoration that announces itself to a screen reader is not decoration.

        📋 THE SAME ARTWORK IS IN `dashboards/kid/KidBackground.tsx`. This SVG
        was duplicated between the two files line for line; both now point at the
        one asset, but the duplication itself is still there and a third copy is
        one paste away.
      */}
      <div className="pointer-events-none fixed bottom-0 left-0 right-0 z-0 w-full">
        <img
          src="/images/meadow-band.webp"
          alt=""
          aria-hidden="true"
          width={2172}
          height={462}
          decoding="async"
          className="block h-auto w-full"
        />
      </div>
    </>
  );
}
